import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { inventoryAPI, categoryAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const InventoryList = () => {
  const { t, i18n } = useTranslation();
  const { showError } = useNotification();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadCategories();
    loadItems();
  }, [search, showLowStock]);

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading categories');
    }
  };

  const getCategoryName = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    if (!category) return categoryName;
    return i18n.language === 'ar' && category.name_ar ? category.name_ar : category.name;
  };

  const getItemName = (item) => {
    return i18n.language === 'ar' && item.item_name_ar ? item.item_name_ar : item.item_name;
  };

  const loadItems = async () => {
    try {
      const params = { search };
      if (showLowStock) params.low_stock = true;
      const response = await inventoryAPI.getAll(params);
      setItems(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    try {
      await inventoryAPI.delete(id);
      loadItems();
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('inventory.title')}</h2>
          <p className="text-gray-600 mt-1">{t('inventory.subtitle')}</p>
        </div>
        <Link
          to="/inventory/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500  transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('inventory.addItem')}
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => setShowLowStock(!showLowStock)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              showLowStock
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showLowStock ? t('inventory.showAll') : t('inventory.showLowStock')}
          </button>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-2">{t('common.status')}</div>
            <div className="col-span-3">{t('inventory.itemName')}</div>
            <div className="col-span-2">{t('inventory.quantity')}</div>
            <div className="col-span-2">{t('inventory.minStockLevel')}</div>
            <div className="col-span-2">{t('inventory.costPerUnit')}</div>
            <div className="col-span-1 text-right">{t('common.actions')}</div>
          </div>

          {/* Card Items */}
          {currentItems.map((item) => {
            const isInStock = (item.quantity - item.min_stock_level) > item.min_stock_level;
            return (
              <div key={item.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
                <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  {/* Status */}
                  <div className="col-span-2 flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isInStock ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm font-medium text-gray-900">
                      {isInStock ? t('inventory.inStock') : t('inventory.lowStock')}
                    </span>
                  </div>

                  {/* Item Name & Category */}
                  <div className="col-span-3">
                    <div className="text-sm font-semibold text-gray-900">{getItemName(item)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {getCategoryName(item.category || 'fabric')} • {item.item_code || item.fabric_code}
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="col-span-2">
                    <div className="text-sm font-medium text-gray-900">{item.quantity} {item.unit}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {t('dashboard.remaining')}: {(item.quantity - item.min_stock_level).toFixed(2)}
                    </div>
                  </div>

                  {/* Min Stock */}
                  <div className="col-span-2">
                    <div className="text-sm text-gray-700">{item.min_stock_level} {item.unit}</div>
                  </div>

                  {/* Cost */}
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-gray-900">{t('common.currency')} {item.cost_per_unit || '0.00'}</div>
                    <div className="text-xs text-gray-500">{t('inventory.perUnit')}</div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end gap-2">
                    <Link
                      to={`/inventory/${item.id}`}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('common.edit')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('common.delete')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            {t('salesOrders.showLabel')} <span className="font-medium">{indexOfFirstItem + 1}</span> {t('common.of')}{' '}
            <span className="font-medium">{items.length}</span> {t('inventory.items')}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous')}
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  currentPage === index + 1
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
            <span className="text-sm text-gray-700 ml-2">
              {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, items.length)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryList;
